<template>
    <div>
        <div class="columns">
            <div class="column">
                <div class="slide-deck-shell">
                    <activity-generating-placeholder
                        v-if="isGenerationStatusUiEnabled && (isGenerating || isFailedGeneration)"
                        activity-type="slide-deck"
                        :generation-status="generationStatus"
                        :generation-error="generationError"
                    />

                    <slide-deck-player v-else-if="slideDeck" :slide-deck-id="slideDeck.id" />

                    <div v-else-if="showSlideDeckSkeleton" class="slide-deck-skeleton" aria-live="polite" aria-busy="true">
                        <div class="slide-deck-skeleton__frame" />
                        <div class="slide-deck-skeleton__thumbs">
                            <div v-for="index in 6" :key="index" class="slide-deck-skeleton__thumb" />
                        </div>
                    </div>

                    <article v-else class="message is-warning">
                        <div class="message-body">
                            No Slide Deck linked to this activity.
                        </div>
                    </article>
                </div>
            </div>
        </div>

        <div class="columns">
            <div class="column">
                <e-field>
                    <display-content :content="activityContent.content" />
                </e-field>
            </div>
        </div>

        <div v-if="activity.activity_settings.acknowledgment && !readOnly" class="m-b-md">
            <e-field label="Acknowledgment">
                <e-checkbox v-if="!completed" v-model="userAccepted">
                    {{ activity.activity_content.acknowledgment_message }}
                </e-checkbox>

                <!-- eslint-disable-next-line vue/prefer-true-attribute-shorthand -->
                <e-checkbox v-else disabled model-value>
                    {{ activity.activity_content.acknowledgment_message }}
                </e-checkbox>
            </e-field>
        </div>

        <activity-completion-action-row>
            <e-button
                v-if="userHasAuthorizedAttempt || (!completed && !readOnly)"
                :loading="isLoading"
                :disabled="disable"
                type="is-primary"
                @click="submit"
                @keyup="submit"
            >
                {{ translate('mark_complete') }}
            </e-button>
        </activity-completion-action-row>
    </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { mapState } from 'vuex';
import ActivityCompletionActionRow from '@/components/activity-types/course-activity-types/ActivityCompletionActionRow.vue';
import ActivityGeneratingPlaceholder from '@/components/activity-types/course-activity-types/activities/shared/ActivityGeneratingPlaceholder.vue';
import DisplayContent from '@/components/editor/DisplayContent.vue';
import SlideDeckPlayer from '@/components/slide-decks/SlideDeck.vue';
import { ACTIVITY_GENERATION_STATUS_UI_ENABLED } from '@/constants/activityGenerationStatusFlag.js';
import AttemptAuthorizationMixin from '@/mixins/AttemptAuthorization.mixin.js';
import CourseActivityMixin from '@/mixins/CourseActivity.mixin.js';
import IsLateMixin from '@/mixins/IsLate.mixin.js';
import axios from '@/plugins/axios.js';

const PENDING_GENERATION_STATUSES = ['pending', 'queued', 'processing', 'generating'];
const POLL_INTERVAL_MS = 15000;

export default {
    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },
    name: 'SlideDeck',

    components: {
        ActivityCompletionActionRow,
        ActivityGeneratingPlaceholder,
        DisplayContent,
        SlideDeckPlayer,
    },

    mixins: [IsLateMixin, CourseActivityMixin, AttemptAuthorizationMixin],

    data: () => ({
        slideDeck: null,
        isLoadingDeck: true,
        userAccepted: false,
        generationStatus: null,
        generationError: null,
        statusPollIntervalId: null,
    }),

    props: {
        readOnly: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),

        isGenerationStatusUiEnabled: () => ACTIVITY_GENERATION_STATUS_UI_ENABLED,

        payload() {
            return {
                uuid: this.activity.uuid,
                course_id: this.$route.params.courseId,
                activity_id: this.$route.params.activityId,
                activity_type: this.get('activity_type'),
                activity_submission: { acknowledgment: this.userAccepted },
            };
        },

        disable() {
            if (this.activity.activity_settings.acknowledgment) {
                return this.disabled || !this.userAccepted;
            }

            return this.disabled;
        },

        isGenerating() {
            if (PENDING_GENERATION_STATUSES.includes(this.generationStatus)) return true;
            return !!this.generationStatus && !this.activityContent?.slide_deck_id;
        },

        isFailedGeneration() {
            return this.generationStatus === 'failed';
        },

        showSlideDeckSkeleton() {
            return this.isLoadingDeck && !this.isGenerating && !this.isFailedGeneration && !this.slideDeck;
        },
    },

    watch: {
        completed(completed) {
            if (completed) {
                this.userAccepted = true;
            }
        },

        '$route.params.activityId': function () {
            this.scrollToTop();
        },
    },

    async created() {
        await this.loadActivity();
    },

    mounted() {
        this.scrollToTop();
    },

    beforeUnmount() {
        this.clearStatusPolling();
    },

    methods: {
        scrollToTop() {
            if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        },

        async loadActivity() {
            const { id } = this.$store.getters[`${this.namespace}/get`](this.uuid);
            const endpoint = this.namespace === 'Activities' ? 'activities' : 'activity-templates';

            const { data } = await axios.get(`/api/${endpoint}/${id}?include=contentable.slideDeck`);

            this.generationStatus = data.data.activity_content?.generation_status ?? null;
            this.generationError = data.data.activity_content?.generation_error ?? null;

            const deckData = data.data.activity_content?.slide_deck;
            if (deckData?.reference_id) {
                this.slideDeck = await this.$store.dispatch('SlideDecks/fetchOne', deckData.reference_id);
            }

            this.isLoadingDeck = false;
            this.ensureStatusPolling();
        },

        ensureStatusPolling() {
            if (!this.isGenerating) {
                this.clearStatusPolling();
                return;
            }
            if (this.statusPollIntervalId) return;

            this.statusPollIntervalId = setInterval(() => {
                this.loadActivity().catch(() => {});
            }, POLL_INTERVAL_MS);
        },

        clearStatusPolling() {
            if (this.statusPollIntervalId) {
                clearInterval(this.statusPollIntervalId);
                this.statusPollIntervalId = null;
            }
        },
    },
};
</script>

<style lang="scss" scoped>

.slide-deck-skeleton {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.slide-deck-skeleton__frame,
.slide-deck-skeleton__thumb {
    position: relative;
    overflow: hidden;
    background: #e5e7eb;
    border-radius: 8px;
}

.slide-deck-skeleton__frame {
    width: 100%;
    aspect-ratio: 16 / 9;
}

.slide-deck-skeleton__thumbs {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 8px;
}

.slide-deck-skeleton__thumb {
    width: 100%;
    aspect-ratio: 1 / 1;
}

.slide-deck-skeleton__frame::after,
.slide-deck-skeleton__thumb::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
    animation: slideDeckSkeletonShimmer 1.6s infinite;
}

@keyframes slideDeckSkeletonShimmer {
    100% {
        transform: translateX(100%);
    }
}
</style>
